import { TV2M_HEARTBEAT_INTERVAL_SEC, } from '@apm/contracts';
import { Tv2mHttpError } from '../adapters/interfaces.js';
import { resolveRoute } from './deep-link.js';
import { Tv2mEventQueue } from './event-queue.js';
import { buildNotificationSpec, parseTv2mPushPayload, shouldDisplayNotification, } from './push-payload.js';
import { TV2M_STORAGE_KEYS, } from './types.js';
/** 서버 경로. 한 곳에 모아 둔다 — 흩어 두면 오타가 404 로만 나타난다. */
const PATHS = {
    register: '/v1/device/register',
    heartbeat: '/v1/device/heartbeat',
    subscribe: '/v1/device/webpush/subscribe',
    deferredCheck: '/v1/deferred-link/check',
};
/** 유입 경로별 진입 이벤트. `Record` 라 `Tv2mSource` 에 값이 늘면 여기가 깨진다. */
const ENTRY_EVENTS = {
    tv2m: 'push_clicked',
    tv2m_same_device: 'same_device_handoff_clicked',
    // Deferred 두 값은 `deferred_link_resolved` 로 보내지 않는다 — **그것은 서버가 이미
    // 기록한다**(T5, `deferred.service.ts`). 링크를 푸는 것과 그 결과로 상품 화면이 열리는
    // 것은 다른 사건이고, 같은 이름으로 두 번 세면 QR 퍼널의 전환율이 부풀려진다.
    tv2m_deferred: 'product_page_opened',
    tv2m_same_device_deferred: 'product_page_opened',
};
/**
 * TV2M 모바일 SDK 본체 (계층 1).
 *
 * **DOM·ServiceWorker·fetch 를 참조하지 않는다** (CLAUDE.md 절대 규칙 13). 서버 호출도
 * 저장소도 Push 구독도 전부 어댑터를 거치며, `tsconfig.core.json` 이 이를 컴파일 단계에서
 * 강제한다. 이것이 향후 Android/iOS 포팅의 유일한 근거다.
 *
 * 부수 이득은 `packages/bloom-receiver-sdk` 와 같다 — 이 클래스 전체가 목 어댑터만으로 단위 테스트된다.
 */
export class Tv2mSdk {
    config;
    adapters;
    events;
    baseUrl;
    profile;
    deviceId = '';
    householdId = null;
    heartbeatIntervalSec = TV2M_HEARTBEAT_INTERVAL_SEC;
    firstRun = false;
    /**
     * 서버에 구독까지 붙였는가. **권한 상태에서 추정하지 않는다** — 권한이 `granted` 라도
     * 구독 생성이 실패하거나 서버가 거절할 수 있고, 그때 화면이 "알림이 켜져 있습니다" 를
     * 띄우면 아무것도 오지 않는 이유가 어디에도 남지 않는다 (T4 의 `PushDelivery` 와 같은 판단).
     */
    pushSubscribed = false;
    /**
     * 마지막으로 확인된 유입 컨텍스트. 이후 이벤트가 이 값을 기본으로 물고 나간다.
     *
     * 없으면 `/done` 화면의 `purchase_completed` 가 어느 매칭의 결과인지 알 수 없다 —
     * 구매 완료 화면은 Deep Link 로 들어온 것이 아니라 앱 안에서 이동해 온 것이라
     * URL 에 `match_id` 가 없다. **전환을 측정하는 것이 이 시스템의 목적이므로**
     * 그 값을 화면마다 다시 들고 다니게 두지 않는다.
     */
    context = {};
    constructor(config, adapters) {
        this.config = config;
        this.adapters = adapters;
        this.baseUrl = config.serverUrl.replace(/\/+$/, '');
        this.events = new Tv2mEventQueue(adapters.network, this.baseUrl);
        this.profile = config.platformProfile ?? (adapters.env.platform() === 'web' ? 'web' : 'native');
    }
    /**
     * SDK 를 세우고 기기를 등록한다.
     *
     * > **`docs/09` T6 의 `static initialize(config): Promise<void>` 를 개정했다.**
     * > 두 곳이 성립하지 않는다. (1) 어댑터를 받지 않으면 코어가 웹 구현을 스스로 만들어야
     * > 하는데 그 순간 `tsconfig.core.json` 가드가 깨진다 — 이 패키지의 존재 이유 전부다.
     * > (2) `void` 를 돌려주면 인스턴스를 꺼낼 길이 숨은 싱글턴뿐이고, 싱글턴은 코어에
     * > 가변 전역을 만들어 테스트가 케이스마다 그것을 되돌려야 한다. **인스턴스 메서드
     * > 목록은 원안 그대로다** — 네이티브 포팅이 보는 표면은 바뀌지 않았다.
     *
     * **등록 실패로 던지지 않는다.** 등록이 안 돼도 상품 화면·구매 경로는 전부 동작해야
     * 한다. 실패하면 `householdId` 가 `null` 로 남고, 그 사실이 `Tv2mInitResult` 에 드러난다.
     */
    static async initialize(config, adapters) {
        const sdk = new Tv2mSdk(config, adapters);
        await sdk.bootstrap();
        return sdk;
    }
    async bootstrap() {
        const { storage, env } = this.adapters;
        this.deviceId = resolveDeviceId(storage, () => env.newDeviceId());
        // 첫 실행 판정은 **저장소만 보고 정해진다 — 등록 성패와 무관하다.** 등록이 실패한
        // 실행도 "실행한 것" 이므로, 다음 실행이 다시 첫 실행이 되어 Deferred 복원을 두 번
        // 시도하는 일이 없다. (등록 앞뒤 어디에 두어도 같다 — `register()` 는 던지지 않는다.
        // 변이 테스트에서 순서를 바꿔도 아무 테스트가 깨지지 않는 것으로 확인했다.)
        this.firstRun = storage.get(TV2M_STORAGE_KEYS.launched) === null;
        if (this.firstRun)
            storage.set(TV2M_STORAGE_KEYS.launched, '1');
        await this.register();
    }
    /** 초기화 결과 한 벌. 화면이 등록 성패와 알림 권한을 보고 배지를 그린다. */
    getInitResult() {
        return {
            deviceId: this.deviceId,
            householdId: this.householdId,
            nextHeartbeatInSeconds: this.heartbeatIntervalSec,
            firstRun: this.firstRun,
            pushPermission: this.adapters.push.getPermission(),
            pushSubscribed: this.pushSubscribed,
        };
    }
    getDeviceId() {
        return this.deviceId;
    }
    /** 등록에 성공했을 때만 값이 있다. */
    getHouseholdId() {
        return this.householdId;
    }
    isFirstRun() {
        return this.firstRun;
    }
    /**
     * Push 구독을 만들고 서버에 붙인다. 네이티브의 `registerPushToken` 대응이다.
     *
     * **권한을 요청하지 않는다** (WB-07). 브라우저는 사용자 제스처 직후가 아닌 요청을
     * 거부하거나 조용히 무시하는데, 코어는 어떤 호출이 제스처 안에 있는지 알 수 없다.
     * 요청은 버튼을 그린 화면이 `PushAdapter.requestPermission()` 으로 하고, 이 메서드는
     * 그 뒤에 불린다.
     *
     * > **`docs/09` T6 의 `Promise<void>` 를 개정했다.** `void` 면 화면이 **왜 못 붙였는지를
     * > 알 수 없다.** 알림이 안 오는 이유가 관측 가능해야 한다는 것은 T4 가 `PushDelivery` 에
     * > 발송 수단 없음까지 행으로 남긴 것과 같은 판단이다. iOS 의 미설치 A2HS(`unsupported`)와
     * > 사용자 거부(`denied`)는 화면이 해야 할 안내가 서로 다르다 (WB-08).
     */
    async registerPushSubscription() {
        const { push } = this.adapters;
        const permission = push.getPermission();
        // **구독의 주인이 파트너인 배포에서는 이 경로가 아니다** (WB-13). 여기서 막지 않으면
        // 파트너의 VAPID 키로 만든 구독이 TV2M 에 저장되고, TV2M 은 그것으로 발송할 수 없는데
        // `pushReachable` 만 켜진다 — 알림을 거부한 집과 구분되지 않게 된다.
        if (this.ownsSubscription() === false) {
            return { subscribed: false, permission, reason: 'wrong_mode' };
        }
        if (!push.isSupported())
            return { subscribed: false, permission, reason: 'unsupported' };
        if (permission !== 'granted') {
            return { subscribed: false, permission, reason: 'permission_denied' };
        }
        if (!this.config.vapidPublicKey) {
            return { subscribed: false, permission, reason: 'no_vapid_key' };
        }
        let subscription;
        try {
            subscription = await push.subscribe(this.config.vapidPublicKey);
        }
        catch (e) {
            this.debug('push 구독 실패', e);
            return { subscribed: false, permission, reason: 'subscribe_failed' };
        }
        const body = { deviceId: this.deviceId, subscription };
        try {
            await this.adapters.network.post(`${this.baseUrl}${PATHS.subscribe}`, body);
        }
        catch (e) {
            this.debug('구독 등록 실패', e);
            return { subscribed: false, permission, reason: 'server_error' };
        }
        this.pushSubscribed = true;
        return { subscribed: true, permission };
    }
    /**
     * 구독을 해제한다. 발송처 쪽과 서버 쪽 **둘 다** 지운다.
     *
     * 한쪽만 지우면 서버는 살아 있는 줄 알고 계속 보내고(발송처가 410 을 줄 때까지),
     * 반대로는 브라우저가 구독을 들고 있는데 아무것도 오지 않는다.
     */
    async unregisterPushSubscription() {
        this.pushSubscribed = false;
        try {
            await this.adapters.push.unsubscribe();
        }
        catch (e) {
            this.debug('발송처 구독 해제 실패', e);
        }
        const query = buildQuery({ deviceId: this.deviceId });
        try {
            await this.adapters.network.delete(`${this.baseUrl}${PATHS.subscribe}${query}`);
        }
        catch (e) {
            this.debug('서버 구독 해제 실패', e);
        }
    }
    /** 우리 알림인가. 판정은 `push-payload.ts` 가 한다. */
    isTv2mPush(data) {
        return parseTv2mPushPayload(data) !== null;
    }
    /**
     * Push 를 처리한다. 우리 것이 아니면 `null` — 호출부(Service Worker)는 그때 아무것도
     * 하지 않아야 한다. 남의 알림을 우리 딥링크로 열면 안 된다.
     *
     * 알림 표시는 `NotificationAdapter` 가 있을 때만 한다. Service Worker 밖의 인스턴스는
     * 그것이 없고, 없다고 payload 처리까지 막을 이유는 없다.
     */
    async handleTv2mPush(data) {
        const payload = parseTv2mPushPayload(data);
        if (!payload)
            return null;
        this.context = contextFromPayload(payload);
        this.trackEvent('push_received');
        const { notification } = this.adapters;
        if (notification && shouldDisplayNotification(payload)) {
            try {
                await notification.show(buildNotificationSpec(payload));
                this.trackEvent('push_displayed');
            }
            catch (e) {
                // 알림 표시가 실패해도 payload 는 돌려준다. 화면이 열려 있으면 그쪽이 살린다.
                this.debug('알림 표시 실패', e);
            }
        }
        await this.events.flush();
        return payload;
    }
    /**
     * 진입 URL 을 해석한다. 상품 컨텍스트가 나오면 그것을, 아니면 `null`.
     *
     * 설치 ref(`?ref=tv2m_qr_…`)가 실려 있으면 **저장만 하고 `null` 을 돌려준다** —
     * 그 URL 은 상품을 담고 있지 않다. 복원은 `checkDeferredLink()` 가 한다.
     */
    handleDeepLink(url) {
        const route = this.route(url);
        if (route.kind !== 'product')
            return null;
        this.context = contextFromLink(route.context);
        this.trackEvent(ENTRY_EVENTS[route.context.source]);
        return route.context;
    }
    /**
     * 진입 URL 의 라우팅 결정. 설치 ref 는 여기서 **저장까지** 한다.
     *
     * 저장하는 이유가 이 설계의 핵심이다. QR 스캔은 `/install?ref=…` 로 오는데, 사용자는
     * 거기서 홈 화면에 추가(A2HS)한 뒤 **아이콘으로 다시 들어온다.** 그때의 시작 URL 은
     * manifest 의 `start_url` 이라 `ref` 가 없다 — 그 순간 컨텍스트를 잃으면 QR 경로 전체가
     * 무의미해진다. 같은 오리진의 저장소는 설치 전후로 유지되므로 여기가 이어 붙는 자리다.
     */
    route(url) {
        const route = resolveRoute(url);
        if (route.kind === 'install') {
            this.adapters.storage.set(TV2M_STORAGE_KEYS.installRef, route.sessionToken);
        }
        return route;
    }
    /**
     * Deferred Link 를 조회한다 (`docs/08` §3).
     *
     * 저장해 둔 설치 ref 가 있으면 `sessionToken` 으로 싣는다. **접두사는 저장 시점에 이미
     * 떨어져 있다**(`parseQrInstallRef`) — 떼는 책임이 SDK 한 곳에 있어야 하고, 빠뜨렸을 때의
     * 증상이 조용한 복원 실패다 (`docs/08` §5).
     *
     * 나머지 셋(timezone·screen·language)은 지문 재료다. **UA 는 싣지 않는다** — 계약에 없고,
     * 서버가 요청 헤더에서 읽는다. 앱이 자기 UA 를 스스로 말할 필요가 없다.
     *
     * **응답이 오면 토큰을 지운다. `found` 와 무관하다.** 서버가 답한 이상 그 토큰의 운명은
     * 정해졌고(소비됐거나 만료됐거나 애초에 없었거나), 죽은 토큰을 계속 실으면 서버가
     * **지문 폴백으로 내려가지 않는다**(T5 의 명시적 규칙: 토큰이 왔는데 링크가 없으면 지문을
     * 보지 않는다). 즉 죽은 ref 하나가 이후의 모든 복원을 영구히 막는다. 네트워크가 실패했을
     * 때만 남긴다 — 그때는 서버가 아무 말도 하지 않은 것이다.
     */
    async checkDeferredLink() {
        const { env, storage } = this.adapters;
        const sessionToken = storage.get(TV2M_STORAGE_KEYS.installRef);
        const query = buildQuery({
            deviceId: this.deviceId,
            sessionToken: sessionToken ?? undefined,
            timezone: env.timezone() ?? undefined,
            screen: env.screen() ?? undefined,
            language: env.language() ?? undefined,
        });
        let result;
        try {
            result = await this.adapters.network.get(`${this.baseUrl}${PATHS.deferredCheck}${query}`);
        }
        catch (e) {
            this.debug('deferred 조회 실패', e);
            return { found: false };
        }
        storage.remove(TV2M_STORAGE_KEYS.installRef);
        if (result.found)
            this.context = contextFromDeferred(result);
        return result;
    }
    /**
     * 활성 상태를 보고한다.
     *
     * **404 면 한 번 재등록하고 다시 보낸다.** 서버는 등록되지 않은 기기의 heartbeat 를
     * 404 로 거절하는데(T2 — 몰래 만들어 주면 `platform`·`pushProvider` 가 빈 채로 가구에
     * 들어가 Push 대상이 된다), 그러면 저장소에 `deviceId` 를 들고 있는 앱이 서버가 비워진 뒤
     * **영원히 404 를 두드린다.** 그 상태에서는 가구에서 빠져 Tier 2 가 이 폰을 찾지 못한다.
     */
    async heartbeat(appState) {
        try {
            await this.sendHeartbeat(appState);
            return;
        }
        catch (e) {
            if (!(e instanceof Tv2mHttpError) || e.status !== 404) {
                this.debug('heartbeat 실패', e);
                return;
            }
        }
        this.debug('미등록 기기 — 재등록 후 heartbeat 재시도');
        await this.register();
        try {
            await this.sendHeartbeat(appState);
        }
        catch (e) {
            this.debug('재등록 후에도 heartbeat 실패', e);
        }
    }
    /**
     * 이벤트를 기록한다. **던지지 않고 기다리지 않는다** — 전송 실패가 전환을 방해하면 안 된다
     * (`docs/08` §6).
     *
     * `detail` 을 주지 않은 값은 마지막 유입 컨텍스트로 채운다. 명시한 값이 언제나 이긴다.
     */
    trackEvent(type, detail) {
        this.events.enqueue({
            ...this.context,
            ...detail,
            deviceId: this.deviceId,
            type,
            ts: new Date().toISOString(),
        });
    }
    /** 남은 이벤트를 지금 보낸다. 화면을 떠나기 전에 호출부가 부른다. */
    flushEvents() {
        return this.events.flush();
    }
    /** 진단용. 디버그 화면이 "이벤트가 새고 있는가" 를 보는 창이다. */
    getEventStats() {
        return this.events.stats;
    }
    /** 현재 유입 컨텍스트. 디버그 화면과 구매 완료 보고가 읽는다. */
    getContext() {
        return { ...this.context };
    }
    /**
     * 실행 프로파일. `'web'` 이면 Client-side IP 매칭(§2.7)을 시도하지 않는다 (WB-06) —
     * 웹은 공인 IP 를 조회할 수단이 없다. 지금 코드에는 분기가 없고, 네이티브 포팅에서
     * Tier 3 이 붙을 자리를 표시한다.
     */
    getPlatformProfile() {
        return this.profile;
    }
    /**
     * 이 배포에서 TV2M 이 Push 구독을 갖는가 (WB-12·13).
     *
     * 기본은 `true` 다 — 모드를 주지 않는 파트너는 v7 그대로 위탁형이고, 그쪽 동작이
     * 바뀌면 안 된다 (`docs/16` §7.3 의 하위 호환 문장).
     */
    ownsSubscription() {
        return (this.config.integrationMode ?? 'delegated') === 'delegated';
    }
    // ── 내부 ──────────────────────────────────────────────────────
    /**
     * 기기를 등록한다. 실패해도 던지지 않는다.
     *
     * 이미 구독이 있으면 등록에 함께 싣는다 — 권한이 이전 실행에서 떨어져 있는 경우다.
     * **권한을 요청하지도, 새 구독을 만들지도 않는다.** 둘 다 사용자 제스처가 필요한 일이고
     * (WB-07) 초기화는 제스처 밖에서 돈다.
     */
    async register() {
        const { env, push, network } = this.adapters;
        const body = {
            deviceId: this.deviceId,
            platform: env.platform(),
            pushProvider: pushProviderFor(env.platform()),
            appId: this.config.appId,
            networkType: env.networkType(),
        };
        const userAgent = env.userAgent();
        if (userAgent)
            body.userAgent = userAgent;
        // v20. **어댑터가 모르면 싣지 않는다** — `undefined` 로 두면 서버가 이미 채워진 값을
        // 덮지 않는다(`device.service.ts` 의 update 분기). 코어는 `navigator` 를 보지 않는다
        // (절대 규칙 13).
        const displayMode = env.displayMode();
        if (displayMode)
            body.displayMode = displayMode;
        // **federated 는 구독을 싣지 않는다** (WB-13). 그 구독은 파트너의 VAPID 키로 만든
        // 것이라 TV2M 이 발송할 수 없고, 받아 두면 도달 가능성의 주인이 둘이 된다.
        if (this.ownsSubscription() && push.isSupported() && push.getPermission() === 'granted') {
            try {
                const existing = await push.getSubscription();
                if (existing)
                    body.webPushSubscription = existing;
            }
            catch (e) {
                this.debug('기존 구독 조회 실패', e);
            }
        }
        try {
            const res = await network.post(`${this.baseUrl}${PATHS.register}`, body);
            if (!res) {
                this.debug('등록 응답이 비었다');
                return;
            }
            this.householdId = res.householdId;
            this.heartbeatIntervalSec = res.nextHeartbeatInSeconds;
            // 등록 본문에 구독을 함께 실었고 서버가 받았다 — 이 경로로도 구독이 붙는다.
            if (body.webPushSubscription)
                this.pushSubscribed = true;
        }
        catch (e) {
            this.debug('기기 등록 실패', e);
        }
    }
    async sendHeartbeat(appState) {
        const res = await this.adapters.network.post(`${this.baseUrl}${PATHS.heartbeat}`, { deviceId: this.deviceId, appState, networkType: this.adapters.env.networkType() });
        if (res)
            this.heartbeatIntervalSec = res.nextHeartbeatInSeconds;
    }
    /**
     * `enableDebugLog` 일 때만 찍는다. **`console` 을 코어가 참조하지 않는다** —
     * `lib: ES2022` 에 없다. 로그는 어댑터 계층과 화면의 일이므로 여기서는 모아만 둔다.
     */
    debug(message, detail) {
        if (!this.config.enableDebugLog)
            return;
        this.debugLog.push({ message, detail, at: new Date().toISOString() });
        if (this.debugLog.length > DEBUG_LOG_MAX)
            this.debugLog.shift();
    }
    debugLog = [];
    /** 디버그 화면(`?debug=1`)이 읽는다. `enableDebugLog` 가 아니면 늘 비어 있다. */
    getDebugLog() {
        return this.debugLog;
    }
}
const DEBUG_LOG_MAX = 50;
/**
 * `deviceId` 는 저장소에 보관해 재실행 간에 유지한다. 없으면 어댑터가 만든다.
 *
 * **코어에 폴백 생성기를 두지 않는다.** 서버 DTO 가 `@IsUUID()` 라(T2) 형식이 어긋나면
 * 등록이 400 이고, `packages/bloom-receiver-sdk` 의 `Math.random` 기반 `dev-xxxx` 를 흉내 내면 정확히
 * 거기서 막힌다. 만드는 일은 `crypto.randomUUID` 를 가진 어댑터의 몫이다.
 */
function resolveDeviceId(storage, mint) {
    const stored = storage.get(TV2M_STORAGE_KEYS.deviceId);
    if (stored)
        return stored;
    const id = mint();
    storage.set(TV2M_STORAGE_KEYS.deviceId, id);
    return id;
}
/**
 * 플랫폼별 Push 채널. MVP 가 구현한 것은 `webpush` 뿐이지만 값은 정확히 보낸다 —
 * 서버가 이 값으로 발송 어댑터를 고르므로(T4), 안드로이드 기기가 `webpush` 로 등록되면
 * 발송이 조용히 실패한다. `Record` 라 플랫폼이 늘면 여기가 깨진다.
 */
const PUSH_PROVIDER_BY_PLATFORM = {
    web: 'webpush',
    android: 'fcm',
    ios: 'apns',
};
function pushProviderFor(platform) {
    return PUSH_PROVIDER_BY_PLATFORM[platform];
}
function contextFromPayload(payload) {
    return {
        matchId: payload.match_id,
        campaignId: payload.campaign_id,
        productId: payload.product_id,
        triggerType: payload.trigger_type,
        clientType: payload.client_type,
    };
}
function contextFromLink(ctx) {
    const detail = {
        campaignId: ctx.campaignId,
        productId: ctx.productId,
        triggerType: ctx.triggerType,
        source: ctx.source,
    };
    if (ctx.matchId)
        detail.matchId = ctx.matchId;
    return detail;
}
function contextFromDeferred(result) {
    const detail = {};
    if (result.matchId)
        detail.matchId = result.matchId;
    if (result.campaignId)
        detail.campaignId = result.campaignId;
    if (result.productId)
        detail.productId = result.productId;
    if (result.triggerType)
        detail.triggerType = result.triggerType;
    if (result.source)
        detail.source = result.source;
    return detail;
}
/**
 * 쿼리스트링을 만든다. 값이 없는 키는 싣지 않는다 — 서버의 `ValidationPipe` 가
 * `forbidNonWhitelisted` 라 빈 문자열도 값으로 읽히고, 지문 점수에 든다.
 *
 * `URLSearchParams` 를 쓸 수 없다 (`lib: ES2022`).
 */
function buildQuery(params) {
    const pairs = Object.entries(params)
        .filter((entry) => entry[1] !== undefined && entry[1] !== '')
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`);
    return pairs.length === 0 ? '' : `?${pairs.join('&')}`;
}
//# sourceMappingURL=tv2m-client.js.map